import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { userService, documentService } from '../../services/api';
import { Users, FileText, UserCog, TrendingUp, Loader2 } from 'lucide-react';

/**
 * Dashboard per amministratori
 */
function AdminDashboard() {
  const [stats, setStats] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    loadStats();
  }, []);

  const loadStats = async () => {
    try {
      // Carica statistiche in parallelo
      const [usersRes, docsRes] = await Promise.all([
        userService.getAll({ limit: 1 }),
        documentService.getStats(),
      ]);

      setStats({
        totalUsers: usersRes.pagination?.total || 0,
        documents: docsRes.data?.stats || {},
      });
    } catch (error) {
      console.error('Errore caricamento statistiche:', error);
    } finally {
      setIsLoading(false);
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="animate-spin text-primary" size={32} />
      </div>
    );
  }

  const statCards = [
    {
      label: 'Utenti totali',
      value: stats?.totalUsers || 0,
      icon: Users,
      color: 'text-blue-600 bg-blue-100 dark:bg-blue-900',
      link: '/users',
    },
    {
      label: 'Documenti totali',
      value: stats?.documents?.totalDocuments || 0,
      icon: FileText,
      color: 'text-green-600 bg-green-100 dark:bg-green-900',
      link: '/documents',
    },
    {
      label: 'Spazio utilizzato',
      value: formatBytes(stats?.documents?.totalSize || 0),
      icon: TrendingUp,
      color: 'text-purple-600 bg-purple-100 dark:bg-purple-900',
    },
  ];

  return (
    <div className="space-y-4 sm:space-y-6 animate-fadeIn">
      <div>
        <h1 className="text-xl sm:text-2xl font-bold text-foreground">Dashboard Amministratore</h1>
        <p className="text-sm sm:text-base text-muted-foreground">
          Panoramica del sistema
        </p>
      </div>

      {/* Statistiche */}
      <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3 sm:gap-4">
        {statCards.map((stat) => (
          <div
            key={stat.label}
            className="bg-card rounded-lg border border-border p-4 sm:p-6"
          >
            <div className="flex items-center gap-3 sm:gap-4">
              <div className={`p-2.5 sm:p-3 rounded-lg ${stat.color} flex-shrink-0`}>
                <stat.icon size={20} className="sm:w-6 sm:h-6" />
              </div>
              <div className="min-w-0 flex-1">
                <p className="text-xl sm:text-2xl font-bold text-foreground">{stat.value}</p>
                <p className="text-xs sm:text-sm text-muted-foreground truncate">{stat.label}</p>
              </div>
            </div>
            {stat.link && (
              <Link
                to={stat.link}
                className="inline-block mt-3 sm:mt-4 text-sm text-primary hover:underline active:underline py-1 touch-manipulation"
              >
                Visualizza tutto →
              </Link>
            )}
          </div>
        ))}
      </div>

      {/* Azioni rapide */}
      <div className="bg-card rounded-lg border border-border p-4 sm:p-6">
        <h2 className="text-base sm:text-lg font-semibold mb-3 sm:mb-4">Azioni rapide</h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
          <Link
            to="/users/new"
            className="flex items-center gap-3 p-3 sm:p-4 rounded-lg border border-border hover:bg-accent active:bg-accent transition-colors min-h-[52px] touch-manipulation"
          >
            <Users size={20} className="text-primary flex-shrink-0" />
            <span className="text-sm sm:text-base">Nuovo utente</span>
          </Link>
          <Link
            to="/assignments"
            className="flex items-center gap-3 p-3 sm:p-4 rounded-lg border border-border hover:bg-accent active:bg-accent transition-colors min-h-[52px] touch-manipulation"
          >
            <UserCog size={20} className="text-primary flex-shrink-0" />
            <span className="text-sm sm:text-base">Assegna clienti</span>
          </Link>
          <Link
            to="/documents"
            className="flex items-center gap-3 p-3 sm:p-4 rounded-lg border border-border hover:bg-accent active:bg-accent transition-colors min-h-[52px] touch-manipulation"
          >
            <FileText size={20} className="text-primary flex-shrink-0" />
            <span className="text-sm sm:text-base">Tutti i documenti</span>
          </Link>
        </div>
      </div>

      {/* Documenti per tipo */}
      {stats?.documents?.byType && stats.documents.byType.length > 0 && (
        <div className="bg-card rounded-lg border border-border p-4 sm:p-6">
          <h2 className="text-base sm:text-lg font-semibold mb-3 sm:mb-4">Documenti per tipo</h2>
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2 sm:gap-4">
            {stats.documents.byType.map((type) => (
              <div key={type._id} className="text-center p-3 sm:p-4 rounded-lg bg-accent/50">
                <p className="text-lg sm:text-2xl font-bold text-foreground">{type.count}</p>
                <p className="text-xs sm:text-sm text-muted-foreground uppercase truncate">{type._id}</p>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// Helper per formattare bytes
function formatBytes(bytes) {
  if (bytes === 0) return '0 B';
  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
}

export default AdminDashboard;
